/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './EmbedPage';

const EmbedPageManager: ComponentManagerFn<'EmbedPage'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'EmbedPage'>(platform, 'EmbedPage'),
    type: 'EmbedPage',
    name: 'Embed Page',
    isChildAllowed: () => false,
    defaultValue,
    props: {
      embeddedPageId: {
        label: 'Embedded Page ID',
        type: 'expression',
        helpText: 'select a page',
        returnType: async () => 'page',
      },
    },
    category: 'Containers',
    canHaveChildren: () => false,
    getDisplayText: (item) => item.props.embeddedPageId.value,
    icon: { iconName: 'layer-group' },
    description: `**Overview**<br/>
The Embed Page component allows you to render an entirely separate page within the body of your current page. It functions as a dynamic container, pulling in external page layouts and logic to treat them as if they were part of the parent view.
This component is the cornerstone of Modular Architecture in the application. Instead of building a single, monolithic page that becomes difficult to maintain, you can compose a master view (like a Dashboard) from several smaller, reusable sub-pages.

**1. Integration & Configuration**<br/>
The Embed Page component is intentionally minimal. It does not contain its own visual elements; its sole purpose is to serve the content of the target page.
* **Embedded Page ID:** This is the single required property. Select the Page ID of the interface you wish to render. This field supports expressions, allowing you to dynamically change which page is displayed based on user logic or variables.

**2. Strategic Benefits: Parallel Development**<br/>
One of the most critical use cases for Embed Page is enabling parallel team workflows.
* **Constraint:** In the visual designer, typically only one developer can edit a specific page at a time.
* **Solution:** If you are building a massive application with multiple complex tabs (e.g., "Settings," "Profile," "Billing"), working on a single page file would create a bottleneck. By using Embed Page, you can assign different developers to build these sub-pages separately and then load them into a "Master Container" page.
* **Result:** Multiple developers can build different sections of the same UI simultaneously without file locking conflicts.

**3. Design Constraints**<br/>
* **No Children:** Because this component's content is fully defined by the external page it loads, you cannot drag and drop other components inside it.
* **Data Isolation:** The embedded page operates with its own scope. While it renders visually inside the parent, data handling typically remains self-contained within the embedded page logic.`,
  };
};

export default EmbedPageManager;
